import { Badge, Button, Card } from "react-bootstrap"
import { Link } from "react-router-dom"
import { AdditionalServiceType } from "../../../Types/additionalServiceType"
import useUserRefs from "../../../Hooks/UseUsersQuery/UseUserRefs"
import { useState } from "react"
import canAccess from "../../../services/accessService/accessService"
import timeService from "../../../services/time/timeService"
import PropertyTitleText from "../../PropertyEntry/PropertyTitleText"

export default function AdditionalServiceEntry(props: {
    additionalServiceData: AdditionalServiceType,
    fullView?: boolean,
    showActions?: boolean,
    customButtons?: {
        onClick: (additionalServiceData: AdditionalServiceType) => void,
        text: string,
        variant?: string,
        disabled?: boolean,
    }[]
}) {

    const {
        additionalServiceData,
        fullView: initialFullView = false,
        showActions = true,
        customButtons,
    } = props

    const [fullView, setFullView] = useState(initialFullView)
    const userRefs = useUserRefs()

    const {
        _id,
        serviceDate,
        serviceEndDate,
        createdDate,
        createdBy,
        assignedTo,
        propertyId,
        bookingId,
        amenityId,
        serviceType,
        serviceSubType,
        price,
        status,
        customerNote,
        internalNote,
    } = additionalServiceData

    const getStatusVariant = (status: string) => {
        switch (status) {
            // case 'completed': return 'success'
            case 'started': return 'warning'
            // case 'in-progress': return 'warning'
            // case 'confirmed': return 'info'
            case 'completed': return 'info'
            case 'ready': return 'primary'
            case 'canceled': return 'danger'
            default: return 'secondary'
        }
    }

    const getUserDisplayName = (userId: string) => {
        if (userRefs[userId]) {
            return `${userRefs[userId].firstName} ${userRefs[userId].lastName}`
        }
        return userId
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title>
                    {serviceType}
                    {serviceSubType && ` - ${serviceSubType}`}
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                    <PropertyTitleText propertyId={propertyId} shortName={true} />
                </Card.Subtitle>

                <Card.Text className="mb-1 small">
                    <strong>Service Date:</strong> {timeService.getFormattedTime(serviceDate)}
                </Card.Text>
                {serviceEndDate && (
                    <Card.Text className="mb-1 small">
                        <strong>Service End Date:</strong> {timeService.getFormattedTime(serviceEndDate)}
                    </Card.Text>
                )}                
                <Card.Text className="mb-1 small">
                    <strong>Price:</strong> ${Number(price).toFixed(2)}
                </Card.Text>
                <Card.Text className="mb-3 small">
                    <strong>Created:</strong> {timeService.getFormattedTime(createdDate)}
                </Card.Text>

                {fullView && (
                    <>
                        <hr />
                        <Card.Subtitle className="mb-2">
                            <strong>Details:</strong>
                        </Card.Subtitle>
                        <Card.Text className="mb-1 small">
                            <strong>Property ID:</strong> <Link className="ms-2" to={`/property/${propertyId}`}>
                                {propertyId}
                            </Link>
                        </Card.Text>
                        {bookingId && (
                            <Card.Text className="mb-1 small">
                                <strong>Booking ID:</strong> <Link className="ms-2" to={`/booking/${bookingId}`}>
                                    {bookingId}
                                </Link>
                            </Card.Text>
                        )}
                        {amenityId && (
                            <Card.Text className="mb-1 small">
                                <strong>Amenity ID:</strong> <Link className="ms-2" to={`/amenity/${amenityId}`}>
                                    {amenityId}
                                </Link>
                            </Card.Text>
                        )}
                        {createdBy && (
                            <Card.Text className="mb-1 small">
                                <strong>Created By:</strong> {getUserDisplayName(createdBy)}
                            </Card.Text>
                        )}
                        {assignedTo && (
                            <Card.Text className="mb-1 small">
                                <strong>Assigned To:</strong> {getUserDisplayName(assignedTo)}
                            </Card.Text>
                        )}
                        {customerNote && (
                            <Card.Text className="mb-1 small">
                                <strong>Customer Note:</strong><br />
                                {customerNote}
                            </Card.Text>
                        )}
                        {internalNote && (
                            <Card.Text className="mb-1 small">
                                <strong>Internal Note:</strong><br />
                                <span className="text-muted">{internalNote}</span>
                            </Card.Text>
                        )}
                    </>
                )}

                {!fullView && (customerNote || internalNote) && (
                    <Card.Text className="mb-2 small text-muted">
                        {customerNote && "Customer note available"}
                        {customerNote && internalNote && " • "}
                        {internalNote && "Internal note available"}
                    </Card.Text>
                )}

            </Card.Body>

            {showActions && (
                <Card.Footer>

                    <div>
                        <Badge bg={getStatusVariant(status)} className="me-2 mb-2">
                            {status}
                        </Badge>
                    </div>

                    {!initialFullView && canAccess('routes', 'additional-service') && (
                        <Link className="btn btn-primary btn-sm me-2 mb-2" to={`/additional-service/${_id}`}>
                            View
                        </Link>
                    )}
                    {canAccess('routes', 'edit-additional-service') && (
                        <Link className="btn btn-secondary btn-sm me-2 mb-2" to={`/edit-additional-service/${_id}`}>
                            Edit
                        </Link>
                    )}
                    {canAccess('routes', 'delete-additional-service') && (
                        <Link className="btn btn-danger btn-sm me-2 mb-2" to={`/delete-additional-service/${_id}`}>
                            Delete
                        </Link>
                    )}
                    {!initialFullView && (
                        <Button
                            className="me-2 mb-2"
                            variant="secondary"
                            size="sm"
                            onClick={() => setFullView(!fullView)}
                        >
                            {fullView ? 'Hide Details' : 'Show Details'}
                        </Button>
                    )}
                    {customButtons && customButtons.map((btn, idx) => (
                        <Button
                            key={`custom-btn-${idx}`}
                            className="me-2 mb-2"
                            variant={btn.variant || 'primary'}
                            size="sm"
                            onClick={() => btn.onClick(additionalServiceData)}
                            disabled={btn.disabled}
                        >
                            {btn.text}
                        </Button>
                    ))}
                </Card.Footer>
            )}
        </Card>
    )
}
